import { useMemo } from "react";
import { useGetList } from "ra-core";
import { sanitizeMessageBodyForFinancialAccess } from "@/lib/permissions/messageFinancialSanitizer";
import { useCanViewAmounts } from "@/lib/permissions/useMaskedAmount";
import type { TicketMessage } from "@/modules/types";
import { ticketMessagePreviewText } from "@/modules/tickets/ticketMessagePreviewText";

export type TicketListMessagePreview = {
  preview: string;
  /** Latest non-internal message direction for needs_reply refinement. */
  lastClientFacingDirection: "inbound" | "outbound" | null;
};

export const useTicketListMessagePreviews = (
  ticketIds: Array<string | number>,
) => {
  const canViewAmounts = useCanViewAmounts();
  const filter =
    ticketIds.length > 0
      ? { "ticket_id@in": `(${ticketIds.join(",")})` }
      : undefined;

  const { data: messages = [] } = useGetList<TicketMessage>(
    "ticket_messages",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "DESC" },
      filter,
    },
    { enabled: ticketIds.length > 0, staleTime: 15_000 },
  );

  return useMemo(() => {
    const map = new Map<string, TicketListMessagePreview>();
    for (const message of messages) {
      const ticketId = String(message.ticket_id);
      const existing = map.get(ticketId);
      // Auto-replies are not a staff answer, so they never settle Needs reply.
      const direction = message.is_automated
        ? null
        : message.direction === "inbound" || message.direction === "outbound"
          ? message.direction
          : null;

      if (!existing) {
        const rawPreview = ticketMessagePreviewText(message);
        map.set(ticketId, {
          preview: rawPreview
            ? sanitizeMessageBodyForFinancialAccess(rawPreview, canViewAmounts)
            : "",
          lastClientFacingDirection: direction,
        });
        continue;
      }

      if (existing.lastClientFacingDirection == null && direction != null) {
        map.set(ticketId, {
          ...existing,
          lastClientFacingDirection: direction,
        });
      }
    }

    // Drop empty previews but keep direction-only rows via a parallel map API.
    const previewMap = new Map<string, string>();
    const directionMap = new Map<string, "inbound" | "outbound">();
    for (const [ticketId, value] of map.entries()) {
      if (value.preview) previewMap.set(ticketId, value.preview);
      if (value.lastClientFacingDirection) {
        directionMap.set(ticketId, value.lastClientFacingDirection);
      }
    }
    return { previewMap, directionMap };
  }, [canViewAmounts, messages]);
};
